import type { ComputeRouteResponse } from '../types/canvasser'

/**
 * OSRM-1 §3.8. The ONE place the "missing field means available" rule lives, so the hook and
 * the page cannot drift apart.
 */
export function isRoutingUnavailable(routeData: ComputeRouteResponse | null): boolean {
  return routeData != null && routeData.routingAvailable === false
}

export interface RouteUiState {
  routingUnavailable: boolean
  /** True only when an optimized order is in effect AND the backend actually routed. */
  isRouteOptimized: boolean
}

export function deriveRouteUiState(
  routeData: ComputeRouteResponse | null,
  /** An optimized order is in effect (frozen by useWalkRoute). */
  hasOptimizedOrder: boolean,
): RouteUiState {
  const routingUnavailable = isRoutingUnavailable(routeData)
  // `routeData != null` kept: no data yet is never "optimized" (pinned by an existing test case).
  return { routingUnavailable, isRouteOptimized: hasOptimizedOrder && routeData != null && !routingUnavailable }
}

/** [A3 §5] What the map says about the order in effect. Never "optimized" unless it really is. */
export type RouteState = 'pending' | 'locating' | 'optimized' | 'approximateUnavailable' | 'approximate' | 'listOrder' | 'manual'

export function deriveRouteState(p: {
  locked: boolean
  /** The frozen order in effect for this turf + mode (null = none). */
  frozenKind: 'optimized' | 'approximate' | null
  routeData: ComputeRouteResponse | null
  online: boolean
  /** The fallback fired with no GPS fix yet: waiting (up to 10 s) before freezing. */
  awaitingGps: boolean
  /** routeData is the PREVIOUS response kept on screen while the next one loads. With nothing frozen
   *  (e.g. ⚡ in flight) it describes an order no longer in effect, so it never names the state. */
  isPlaceholder?: boolean
}): RouteState {
  if (p.locked) return 'manual'
  if (p.frozenKind === 'optimized') return 'optimized'
  if (p.frozenKind === 'approximate')
    return !p.online || isRoutingUnavailable(p.routeData) ? 'approximateUnavailable' : 'approximate'
  if (p.awaitingGps) return 'locating'
  if (p.routeData == null || p.isPlaceholder) return 'pending'
  // A single unrouted response (no fallback yet) stays undecided; the routing-unavailable chip covers it.
  if (isRoutingUnavailable(p.routeData)) return 'pending'
  // Optimized but not frozen yet (the freeze lands next commit) — the order in effect IS optimized.
  if (p.routeData.optimized) return 'optimized'
  return 'listOrder'
}

export const ROUTE_STATE_LABEL: Record<RouteState, string | null> = {
  pending: null,
  locating: 'Finding your location…',
  optimized: null,
  approximateUnavailable: 'Approximate order — routing unavailable',
  approximate: 'Approximate order',
  listOrder: 'Route in list order',
  manual: null,
}
